import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Card, Grid, Typography, CardMedia, List, ListItem, ListItemText,
  Divider, CardContent, CircularProgress, Alert, Button,
  Dialog, DialogActions, DialogContent, DialogTitle, TextField
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import Carte from './Carte';
import axiosInstance from '../../Service/axiosInstance';

const PackageDetails = ({ setUser }) => {
  const { idPackage } = useParams();
  const navigate = useNavigate();

  // États liés aux données du package
  const [packageDetails, setPackageDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // États liés à la demande
  const [openModal, setOpenModal] = useState(false);
  const [nombrePersonne, setNombrePersonne] = useState('');
  const [nombreEnfant, setNombreEnfant] = useState('');
  const [nombreBebe, setNombreBebe] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [requete, setRequete] = useState('');
  const [loadingDevis, setLoadingDevis] = useState(false);
  const [message, setMessage] = useState("");

  // Récupération des infos utilisateur + détails du package
  useEffect(() => {
    let isMounted = true;
    dayjs.locale('fr');

    const storedUser = localStorage.getItem('user');
    const tokenUser = localStorage.getItem('token');

    if (storedUser && tokenUser) {
      setUser(JSON.parse(storedUser));
    } else {
      navigate(`/login?idPackage=${idPackage}`);
      return;
    }

    axiosInstance.get(`/yourtravelshop/package/details/${idPackage}`)
      .then((response) => {
        if (isMounted) {
          setPackageDetails(response.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.error("Erreur récupération du package :", err);
          setError("Impossible de charger les détails du package.");
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [idPackage, navigate, setUser]);

  // Fonctions modales
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  // Soumission du formulaire
  const handleSubmit = () => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!nombrePersonne || !dateDebut || !dateFin || !nombreBebe || !nombreEnfant) {
      alert("Veuillez remplir tous les champs.");
      return;
    }

    if (!user || !user.id || !user.role || !user.role.idRole) {
      alert("Erreur : Informations utilisateur incomplètes.");
      console.error("Utilisateur invalide :", user);
      return;
    }

    setLoadingDevis(true);

    const demande = {
      packages: { idPackage: Number(idPackage) },
      nombrePersonne: Number(nombrePersonne),
      nombreEnfant: Number(nombreEnfant),
      nombreBebe: Number(nombreBebe),
      dateDebut,
      dateFin,
      requete
    };

    axiosInstance.post('/yourtravelshop/reservation/demande', demande)
      .then(() => {
        setMessage("Demande de devis envoyée avec succès !");
        setOpenModal(false);
      })
      .catch((err) => {
        console.error("Erreur lors de la demande :", err);
        setError("Une erreur est survenue lors de l'envoi de votre demande.");
      })
      .finally(() => {
        setLoadingDevis(false);
      });
  };

  // Affichage pendant chargement
  if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 5 }} />;
  if (error) return <Alert severity="error" sx={{ mt: 5, textAlign: 'center' }}>{error}</Alert>;
  if (!packageDetails) return <Alert severity="info" sx={{ mt: 5, textAlign: 'center' }}>Aucun détail trouvé.</Alert>;

  return (
<div className="trending">
  
<Container sx={{ mt: 5, width: '100%' }}>
  <Card sx={{ p: 3, bgcolor: '#f3e5f5', borderRadius: 2 }}> {/* Fond de carte violet pâle avec bord arrondi */}
    <Grid container spacing={3}>
      
      {/* Image Principale */}
      <Grid item xs={12} md={5}>
        <CardMedia
          component="img"
          height="300"
          image={packageDetails.prospectus?.stockage || "/assets/img/imageNonDisponible.jpg"}
          alt={packageDetails.nom || "Image non disponible"}
          sx={{ borderRadius: 2 }}
        />
      </Grid>

      {/* Informations Générales */}
      <Grid item xs={12} md={7}>
        <CardContent>
          {message && <p style={{ color: "green", fontWeight: "bold" }}>{message}</p>}
          {error && <p style={{ color: "red", fontWeight: "bold" }}>{error}</p>}
          
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#6a1b9a' }}> {/* Violet foncé */}
            {packageDetails.nom}
          </Typography>
          <Typography variant="h6" color="primary">{packageDetails.typeOffre.type}</Typography>
          
          <Typography 
  variant="h6" 
  sx={{ 
    mt: 2, 
    fontWeight: 'bold', 
    color: '#008b8b', // Violet foncé, ou ajustez à votre choix
    letterSpacing: 1, // Un peu d'espace entre les lettres pour le style
    lineHeight: 1.6 
  }}
>
  {packageDetails.description}
</Typography>

          <Typography variant="body1" sx={{ mt: 2 }}>
          {packageDetails.details.split('\n').map((line, index) => {
            // Vérifie si la ligne contient un jour
            if (line.toLowerCase().includes('jour')) {
              return (
                <Typography key={index} variant="body1" sx={{ fontWeight: 'bold' }}>
                  {line}
                </Typography>
              );
            } else {
              return (
                <Typography key={index} variant="body1">
                  {line}
                </Typography>
              );
            }
          })}
        </Typography>    
          <Typography variant="h5" sx={{ mt: 2, fontWeight: 'bold', color: '#d81b60' }}> {/* Rose foncé */}
            A partir de {packageDetails.prix} Ar
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Min. Pers.:</strong> {packageDetails.minPers} | <strong>Max. Pers.:</strong> {packageDetails.maxPers}
          </Typography>
          {packageDetails.placesDisponibles != null && (
            <Typography
              variant="body2"
              sx={{ mt: 1, color: packageDetails.placesDisponibles > 0 ? 'green' : 'red' }}
            >
              <strong>
                {packageDetails.placesDisponibles > 0 ? "Places disponibles" : "Sold out"}
              </strong>
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Dates: </strong> Du {dayjs(packageDetails.dateDebut).format('DD MMMM YYYY')} au {dayjs(packageDetails.dateFin).format('DD MMMM YYYY')}
          </Typography>

          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mt: 3 }}
            onClick={handleOpenModal}
          >
            Demander un devis
          </Button>
        </CardContent>
      </Grid>
    </Grid>

    <Divider sx={{ my: 3 }} />
    
    {/* Carte */}
    <Carte latitude={packageDetails.latitude} longitude={packageDetails.longitude} />
    <br />

    {/* Activités, Services Inclus et Exclus */}
    <Grid container spacing={3}>
      {[{ title: "Activités", data: packageDetails.activites },
        { title: "Services Inclus", data: packageDetails.services },
        { title: "Services Optionnels", data: packageDetails.excludedServices }].map((section, index) => (
        <Grid item xs={12} md={4} key={index}>
          <Card variant="outlined" sx={{ bgcolor: 'white', p: 2, borderColor: '#9575cd', borderRadius: '12px' }}> {/* Violet clair */}
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6a1b9a' }}>{section.title}</Typography>
              <List dense>
                {section.data.map((item) => (
                  <ListItem key={item.id}>
                    <ListItemText primary={item.nom} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    <Divider sx={{ my: 3 }} />

    {/* Conditions de Paiement */}
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card variant="outlined" sx={{ bgcolor: 'white', p: 2, borderColor: '#9575cd', borderRadius: '12px' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6a1b9a', mb: 2 }}>
              Conditions
            </Typography>
            <List dense>
              {packageDetails.conditionsPaiement.map((condition, index) => (
                <ListItem key={index}>
                  <ListItemText primary={condition.description} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    <Divider sx={{ my: 3 }} />

    {/* Couverture avec Swiper */}
    <Grid item xs={12}>
      {packageDetails.couverture && packageDetails.couverture.length > 0 ? (
        <Swiper
          spaceBetween={10} // Espace entre les images
          slidesPerView={1} // Afficher une image à la fois
          loop={true} // Permet le défilement infini
          autoplay={{ delay: 3000 }} // Autoplay avec un délai de 3 secondes
          pagination={{ clickable: true }} // Pagination
          navigation // Navigation avec flèches
          allowTouchMove={true} // Permet le swipe avec le doigt
        >
          {packageDetails.couverture.map((cover) => (
            <SwiperSlide key={cover.idPhoto}>
              <CardMedia
                component="img"
                image={cover.stockage}
                alt={`Couverture ${cover.idPhoto}`}
                sx={{ width: '100%', borderRadius: 2 }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 3 }}>
          Aucune image de couverture disponible
        </Typography>
      )}
    </Grid>

    {/* Modal pour demander un devis */}
    <Dialog open={openModal} onClose={handleCloseModal}>
  <DialogTitle>Demander un devis</DialogTitle>
  <DialogContent>
    <TextField
          autoFocus
          margin="dense"
          label="Nombre d'adultes (10 ans et plus)"
          type="number"
          fullWidth
          variant="outlined"
          value={nombrePersonne}
          inputProps={{ min: 0 }}
          onChange={(e) => setNombrePersonne(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          autoFocus
          margin="dense"
          label="Nombre d'enfant (moins de 10 ans)"
          type="number"
          fullWidth
          variant="outlined"
          inputProps={{ min: 0 }}
          value={nombreEnfant}
          onChange={(e) => setNombreEnfant(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          autoFocus
          margin="dense"
          label="Nombre de bébé (moins de 2 ans)"
          type="number"
          fullWidth
          variant="outlined"
          value={nombreBebe}
          inputProps={{ min: 0 }}
          onChange={(e) => setNombreBebe(e.target.value)}
          sx={{ mb: 2 }}
        />
      <TextField
      margin="dense"
      label="Date de début"
      type="date"
      fullWidth
      variant="outlined"
      value={dateDebut}
      onChange={(e) => setDateDebut(e.target.value)}
      sx={{ mb: 2 }}
      InputLabelProps={{ shrink: true }}
    />
    <TextField
      margin="dense"
      label="Date de fin"
      type="date"
      fullWidth
      variant="outlined"
      value={dateFin}
      onChange={(e) => setDateFin(e.target.value)}
      sx={{ mb: 2 }}
      InputLabelProps={{ shrink: true }}
    />
    <TextField
      margin="dense"
      label="Requete"
      type="text"
      fullWidth
      variant="outlined"
      value={requete}
      onChange={(e) => setRequete(e.target.value)}
      sx={{ mb: 2 }}
    />

    {loadingDevis && (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
        <CircularProgress size={24} />
      </div>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCloseModal} color="secondary" disabled={loadingDevis}>
      Annuler
    </Button>
    <Button onClick={handleSubmit} color="primary" disabled={loadingDevis}>
      {loadingDevis ? 'Envoi...' : 'Envoyer la demande'}
    </Button>
  </DialogActions>
</Dialog>  </Card>
</Container>

    </div>
  );
};

export default PackageDetails;
